import { useEffect, useState } from "react";
import { GOOGLE_FONTS_CSS_URL } from "@/lib/cms/cms-theme.constants";

const STYLESHEET_ID = "cms-google-fonts";
const PRELOAD_ID = "cms-google-fonts-preload";

/*
 * Feuille Google Fonts des polices CMS (sélecteurs d'apparence, canevas Puck),
 * chargée sans jamais bloquer le canevas.
 *
 * Le canevas Puck (AutoFrame) recopie dans son iframe les feuilles de la page
 * présentes à son montage et reste sur son spinner tant qu'elles ne sont pas
 * chargées ; une feuille ajoutée ensuite dans <head> PENDANT ce chargement est
 * comptée sans jamais être attendue → spinner permanent. D'où :
 *  - préchargement (`rel="preload"`, ignoré par Puck), puis vraie feuille une
 *    fois en cache : si Google est lent ou bloqué, seules ces polices manquent ;
 *  - vraie feuille posée dans <body>, que Puck n'observe pas après son montage
 *    (présente avant, elle est recopiée et se charge depuis le cache) ;
 *  - dans le canevas : recopiée par Puck si elle était déjà dans la page à son
 *    montage, sinon posée par `useCmsGoogleFontsInFrame` une fois en cache
 *    (dans <body> aussi : Puck vide le <head> de l'iframe à la recopie).
 * Un `<link>` plutôt qu'un `fetch` : la CSP du BO autorise fonts.googleapis.com
 * en `style-src`, pas en `connect-src`.
 */

let pending: Promise<boolean> | null = null;

function hasStylesheet(doc: Document): boolean {
  return Array.from(
    doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
  ).some((link) => link.getAttribute("href") === GOOGLE_FONTS_CSS_URL);
}

function appendStylesheet(doc: Document): void {
  if (hasStylesheet(doc)) return;
  const link = doc.createElement("link");
  link.id = STYLESHEET_ID;
  link.rel = "stylesheet";
  link.href = GOOGLE_FONTS_CSS_URL;
  doc.body.appendChild(link);
}

/**
 * Charge la feuille dans la page (idempotent).
 * @returns `true` une fois la feuille en cache et posée, `false` si Google n'a
 * pas répondu (un prochain appel retentera).
 */
export function loadCmsGoogleFonts(): Promise<boolean> {
  if (typeof document === "undefined") return Promise.resolve(false);
  if (hasStylesheet(document)) return Promise.resolve(true);
  if (pending) return pending;
  pending = new Promise<boolean>((resolve) => {
    const preload = document.createElement("link");
    preload.id = PRELOAD_ID;
    preload.rel = "preload";
    preload.as = "style";
    preload.href = GOOGLE_FONTS_CSS_URL;
    preload.onload = () => {
      appendStylesheet(document);
      preload.remove();
      resolve(true);
    };
    preload.onerror = () => {
      preload.remove();
      pending = null;
      resolve(false);
    };
    document.head.appendChild(preload);
  });
  return pending;
}

/** Pose la feuille dans le document du canevas Puck une fois en cache. */
export function useCmsGoogleFontsInFrame(frameDocument?: Document): void {
  // Déjà dans la page au montage : Puck la recopie lui-même dans le canevas.
  const [copiedByPuck] = useState(
    () => typeof document !== "undefined" && hasStylesheet(document),
  );
  useEffect(() => {
    if (!frameDocument || copiedByPuck) return;
    let cancelled = false;
    void loadCmsGoogleFonts().then((loaded) => {
      if (loaded && !cancelled) appendStylesheet(frameDocument);
    });
    return () => {
      cancelled = true;
    };
  }, [frameDocument, copiedByPuck]);
}
